/**
 * User subscription tier types
 * - FREE: 10MB file size limit
 * - SIGNED_IN: 5MB file size limit
 * - SUBSCRIBED: Unlimited file size
 */
export enum UserTier {
  FREE = 'free',
  SIGNED_IN = 'signed_in',
  SUBSCRIBED = 'subscribed',
}

/**
 * User profile stored in Supabase
 */
export interface UserProfile {
  id: string;
  email: string;
  tier: UserTier;
  subscription_id?: string;
  has_premium: boolean;
  last_payment_date?: string;
  created_at: string;
  updated_at: string;
}

/**
 * File share history entry (metadata only, no actual file)
 * user_id can be null for anonymous users
 */
export interface FileShareHistory {
  id: string;
  user_id: string | null;
  filename: string;
  file_size: number;
  peer_id: string;
  peer_name?: string;
  transfer_type: 'sent' | 'received';
  created_at: string;
}

/**
 * Peer discovery method
 */
export enum DiscoveryMethod {
  LINK = 'link',
  LOCAL = 'local',
}

/**
 * WebRTC peer connection data
 */
export interface PeerData {
  id: string;
  name: string;
  connected?: boolean;
  signal?: any; // SimplePeer.SignalData
  discoveryMethod?: DiscoveryMethod;
}

/**
 * File transfer state
 */
export enum TransferStatus {
  IDLE = 'idle',
  INITIATING = 'initiating',
  CONNECTING = 'connecting',
  TRANSFERRING = 'transferring',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/**
 * Active file transfer information
 */
export interface FileTransfer {
  id: string;
  file: File;
  peer: PeerData;
  status: TransferStatus;
  progress: number; // 0-100
  bytesTransferred: number;
  totalBytes: number;
  startTime: number;
  error?: string;
}

/**
 * WebRTC signaling message types
 */
export enum SignalType {
  OFFER = 'offer',
  ANSWER = 'answer',
  ICE_CANDIDATE = 'ice_candidate',
  PEER_JOINED = 'peer_joined',
  PEER_LEFT = 'peer_left',
}

/**
 * Signaling message structure
 */
export interface SignalMessage {
  type: SignalType;
  from: PeerData;
  to?: string;
  data?: any;
  timestamp: number;
}

/**
 * File size limits based on user tier (in bytes)
 */
export const FILE_SIZE_LIMITS: Record<UserTier, number | null> = {
  [UserTier.FREE]: 10 * 1024 * 1024, // 10MB
  [UserTier.SIGNED_IN]: 5 * 1024 * 1024, // 5MB
  [UserTier.SUBSCRIBED]: null, // Unlimited
};

/**
 * Subscription configuration for Lemon Squeezy
 */
export interface SubscriptionConfig {
  storeId: string;
  productId: string;
}

/**
 * Application configuration from environment variables
 */
export interface AppConfig {
  supabase: {
    url: string;
    anonKey: string;
  };
  lemonSqueezy: SubscriptionConfig;
}
